import { Field, NumberField } from '@base-ui/react';
import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { autofillSurface, focusRing, hoverable } from '@/design-system/interaction-states';

import { typographyVariantStyles } from '../../typography-variants';
import type { HelperState } from '../Helper/types';
import type { InputNumberAlign, InputNumberSize } from './types';

const sizeConfig: Record<
  InputNumberSize,
  { height: number; borderRadius: number; padding: string; typography: 'body1' | 'body2' | 'body3' }
> = {
  sm: { height: 32, borderRadius: 8, padding: '6px 12px', typography: 'body3' },
  md: { height: 40, borderRadius: 12, padding: '8px 12px', typography: 'body2' },
  lg: { height: 48, borderRadius: 12, padding: '8px 16px', typography: 'body1' },
};

function getBorderColor(theme: Theme, state: HelperState): string {
  switch (state) {
    case 'destructive':
      return theme.colors.error.border;
    case 'success':
      return theme.colors.success.border;
    default:
      return theme.colors.border.default;
  }
}

export const StyledRoot = styled(Field.Root, {
  shouldForwardProp: (prop) => prop !== '$fullWidth',
})<{ $fullWidth: boolean }>(({ $fullWidth }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  width: $fullWidth ? '100%' : 'fit-content',
  minWidth: 0,

  '&[data-disabled]': {
    opacity: 0.5,
    pointerEvents: 'none',
  },
}));

export const StyledGroup = styled(NumberField.Group, {
  shouldForwardProp: (prop) => !['$state', '$fullWidth', '$size'].includes(prop as string),
})<{ $state: HelperState; $fullWidth: boolean; $size: InputNumberSize }>(
  ({ theme, $state, $fullWidth, $size }) => ({
    display: 'flex',
    alignItems: 'stretch',
    width: $fullWidth ? '100%' : 288,
    maxWidth: '100%',
    height: sizeConfig[$size].height,
    boxSizing: 'border-box',
    borderRadius: sizeConfig[$size].borderRadius,
    backgroundColor: theme.colors.background.overlay,
    border: `1px solid ${getBorderColor(theme, $state)}`,
    transition: 'box-shadow 0.15s ease',

    ...focusRing(theme, { within: true }),
  }),
);

export const StyledInput = styled(NumberField.Input, {
  shouldForwardProp: (prop) => prop !== '$align' && prop !== '$size',
})<{ $align: InputNumberAlign; $size: InputNumberSize }>(({ theme, $align, $size }) => ({
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  padding: sizeConfig[$size].padding,
  textAlign: $align,
  color: theme.colors.text.primary,
  ...typographyVariantStyles[sizeConfig[$size].typography],
  fontWeight: 500,

  '&::placeholder': {
    color: theme.colors.text.secondary,
  },

  ...autofillSurface(theme),
}));

export const StyledStepperGroup = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 8px',
  flexShrink: 0,
});

const stepperButtonStyles = (theme: Theme) => ({
  ...hoverable(theme, 'surfaceRaised'),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 4,
  borderRadius: theme.radius[4],
  border: 'none',
  backgroundColor: theme.colors.background.raised,
  color: theme.colors.icon.primary,
  cursor: 'pointer',
  flexShrink: 0,

  '& svg': {
    width: 14,
    height: 14,
  },

  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});

export const StyledIncrement = styled(NumberField.Increment)(({ theme }) =>
  stepperButtonStyles(theme),
);

export const StyledDecrement = styled(NumberField.Decrement)(({ theme }) =>
  stepperButtonStyles(theme),
);
